/*
 * -----------------------------------------------------------------------
 *  <copyright file="panel-frame/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, createContext } from 'react';
import Fade from '@mui/material/Fade';
import { useBoolean } from 'hooks';
import { PanelFields } from 'queries/generated/fragments.generated';
import PanelDisplay from 'views/components/central-monitoring/panels/panel-display';
import ItemWrapper from 'views/components/core/item-wrapper';
import Loading from 'views/components/core/loading';
import './styles.scss';
import HighchartsReact from 'highcharts-react-official';

interface PanelFrameInterface {
    title?: string;
    children?: any;
    panel?: any;
    expandable?: boolean;
    fullscreen?: boolean;
    toggleLegend?: boolean;
    print?: boolean;
    download?: boolean;
    menuItems?: any;
    onExpand?: Function;
    siteId?: string;
    customKey?: string;
    headerColor?: string;
    canEdit?: boolean;
    height?: number;
    onOpenAddPanelDialog?: (panel: PanelFields) => void;
}

export const PanelFrameContext = createContext<PanelFrameInterface | null>(null);

const PanelFrame = ({
    panel,
    title,
    children,
    expandable,
    fullscreen = true,
    print = true,
    download = true,
    menuItems,
    onExpand,
    siteId,
    toggleLegend = true,
    customKey,
    headerColor,
    canEdit,
    onOpenAddPanelDialog,
}: PanelFrameInterface) => {
    const [chartRef, setChartRef] = useState<HighchartsReact.RefObject | null>(null);
    const hideLegend = useBoolean(true);
    const [expandChartRef, setExpandChartRef] = useState<HighchartsReact.RefObject | null>(null);

    const expanded = useBoolean(false);
    const loading = useBoolean(true);
    const panelMenu = [] as any;

    if (fullscreen)
        panelMenu.push({
            label: 'cm:Fullscreen',
            icon: ['fal', 'expand-alt'],
            onClick: () => {
                if (chartRef?.chart) chartRef.chart.fullscreen.open();
            },
            enabled: true,
        });

    if (toggleLegend)
        panelMenu.push({
            label: 'cm:toggleLegend',
            icon: ['fal', 'low-vision'],
            onClick: () => {
                if (chartRef?.chart) {
                    if (hideLegend.value) {
                        chartRef.chart.legend.update({ enabled: true });
                        chartRef.chart.subtitle.show();
                    } else {
                        chartRef.chart.legend.update({ enabled: false });
                        chartRef.chart.subtitle.hide();
                    }
                    hideLegend.toggle();
                }
                if (expandChartRef?.chart && expanded.value) {
                    expandChartRef.chart.legend.update({ enabled: !expandChartRef.chart.legend.options.enabled });
                    if (expandChartRef.chart.legend.options.enabled) expandChartRef.chart.subtitle.show();
                    else expandChartRef.chart.subtitle.hide();
                }
            },
            enabled: true,
        });

    if (panel)
        panelMenu.push({
            label: 'cm:panelSetup',
            icon: ['fal', 'edit'],
            onClick: () => {
                onOpenAddPanelDialog?.(panel);
            },
            enabled: true,
        });

    if (print)
        panelMenu.push({
            label: 'cm:PrintChart',
            icon: ['fal', 'print'],
            onClick: () => {
                if (expandChartRef?.chart && expanded.value) expandChartRef.chart.print();
                else if (chartRef?.chart) chartRef.chart.print();
            },
            enabled: true,
        });

    if (download) {
        panelMenu.push({
            label: 'cm:DownloadPNG',
            icon: ['fal', 'file-image'],
            onClick: () => {
                if (expandChartRef?.chart && expanded.value) expandChartRef.chart.exportChartLocal();
                else if (chartRef?.chart) chartRef.chart.exportChartLocal();
            },
            enabled: true,
        });
        panelMenu.push({
            label: 'cm:DownloadJPEG',
            icon: ['far', 'file-image'],
            onClick: () => {
                if (expandChartRef?.chart && expanded.value)
                    expandChartRef.chart.exportChartLocal({ type: 'image/jpeg' });
                else if (chartRef?.chart) chartRef.chart.exportChartLocal({ type: 'image/jpeg' });
            },
            enabled: true,
        });

        /* Removed PDFs due to formatting issues - temporarily - KM
        panelMenu.push({
            label: 'cm:DownloadPDF',
            icon: ['fal', 'file-pdf'],
            onClick: () => {
                if(expandChartRef?.chart && expanded.value)
                    expandChartRef.chart.exportChartLocal({ type: 'application/pdf' });
                else if(chartRef?.chart)
                    chartRef.chart.exportChartLocal({ type: 'application/pdf' });
            },
            enabled: true
        });
        */

        panelMenu.push({
            label: 'cm:DownloadSVG',
            icon: ['fal', 'file-code'],
            onClick: () => {
                if (expandChartRef?.chart && expanded.value)
                    expandChartRef.chart.exportChartLocal({ type: 'image/svg+xml' });
                else if (chartRef?.chart) chartRef.chart.exportChartLocal({ type: 'image/svg+xml' });
            },
            enabled: true,
        });
    }

    (menuItems || []).forEach(({ label, icon, onClick, enabled }: any) =>
        panelMenu.splice(0, 0, {
            label,
            icon,
            onClick,
            enabled,
        }),
    );

    if (expandable && !expanded.value) {
        panelMenu.splice(0, 1, {
            label: 'cm:ExpandChart',
            icon: ['fal', 'expand-alt'],
            onClick: () => {
                expanded.setTrue();
                onExpand?.(true);
            },
            enabled: true,
        });
    } else if (expandable && expanded.value) {
        panelMenu.splice(0, 1, {
            label: 'cm:collapseChart',
            icon: ['fal', 'compress-alt'],
            onClick: () => {
                expanded.setFalse();
                onExpand?.(false);
            },
            enabled: true,
        });
    }

    return (
        <div key={customKey}>
            <ItemWrapper
                title={title ?? ''}
                actions={panelMenu}
                hideItemNumber
                customStyle={headerColor ? { backgroundColor: headerColor } : null}
                customChildStyle={{ backgroundColor: '#fff' }}
            >
                {!chartRef && loading.value && <Loading inside />}
                <div style={!chartRef && loading.value ? { display: 'none' } : {}}>
                    {children && typeof children === 'function' && children(setChartRef)}
                    {panel && (
                        <PanelDisplay
                            setRef={setChartRef}
                            setLoadingComplete={loading.setFalse}
                            type={panel.panelType}
                            chartConfig={panel.graph}
                            modelConfig={panel.modelParams}
                            panelData={panel.panelData}
                            hidePlotTitle
                            entityName={title}
                            siteId={siteId}
                            hideLegend={hideLegend.value}
                        />
                    )}
                </div>
            </ItemWrapper>
            {/* for expand chart functionality */}
            {expandable ? (
                <Fade in={expanded.value}>
                    <div className="absolute-chart-display">
                        <ItemWrapper title={title ?? ''} actions={panelMenu} hideItemNumber>
                            <div style={!chartRef ? { display: 'none' } : {}}>
                                {children && typeof children === 'function' && children(setChartRef)}
                                {panel && (
                                    <PanelFrameContext.Provider value={{ height: 750 }}>
                                        <PanelDisplay
                                            setRef={setExpandChartRef}
                                            type={panel.panelType}
                                            chartConfig={panel.graph}
                                            modelConfig={panel.modelParams}
                                            panelData={panel.panelData}
                                            hidePlotTitle
                                            entityName={title}
                                            siteId={siteId}
                                        />
                                    </PanelFrameContext.Provider>
                                )}
                            </div>
                        </ItemWrapper>
                    </div>
                </Fade>
            ) : null}
            {/* end */}
        </div>
    );
};

export default React.memo(PanelFrame);
